import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { useActiveTenant } from "../hooks/useActiveTenant";
import NoTenantSelected from "../components/NoTenantSelected";
import { fetchSites, createSite as apiCreateSite, updateSite as apiUpdateSite, deleteSite as apiDeleteSite, fetchSiteImpact, fetchRoomsWithSchedule, fetchRoomsWeekRange, createRoom as apiCreateRoom, updateRoom as apiUpdateRoom, deleteRoom as apiDeleteRoom, fetchRoomSessionCount, patchSessionRoom, fetchUnassignedSessions, fetchRoomAssignmentCheck, bulkAssignRoom as apiBulkAssignRoom, fetchRoomFeatures, addRoomFeature, deleteRoomFeature } from "../api/rooms";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  IconButton,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import AddIcon from "@mui/icons-material/Add";
import BusinessIcon from "@mui/icons-material/Business";
import PeopleIcon from "@mui/icons-material/People";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import GridViewIcon from "@mui/icons-material/GridView";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BuildIcon from "@mui/icons-material/Build";
import BoltIcon from "@mui/icons-material/Bolt";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

// ── Design tokens ─────────────────────────────────────────────────────────────
const TC = {
  ink: "var(--color-ink)",
  ink2: "var(--color-ink-2)",
  ink3: "var(--color-ink-3)",
  ink4: "var(--color-ink-4)",
  line: "var(--color-hairline-cloud)",
  line2: "var(--color-surface-press-light)",
  surface2: "var(--color-surface-press-light)",
  surface3: "var(--color-surface-press-light)",
  orange: "#C2410C",
  orange10: "rgba(194,65,12,.10)",
};

function makeTC(theme) {
  const p = theme.palette.primary.main;
  const s = theme.palette.secondary.main;
  const a = theme.palette.warning.main;
  return {
    ...TC,
    primary: p,
    primary700: theme.palette.primary.dark,
    primary10: alpha(p, 0.08),
    green: s,
    green10: alpha(s, 0.10),
    amber: a,
    amber10: alpha(a, 0.10),
  };
}

function getCatKey(category) {
  if (!category) return "gen";
  const c = category.toLowerCase();
  if (c.includes("officer")) return "fo";
  if (c.includes("hazmat") || c.includes("hazardous")) return "hm";
  if (c.includes("ems") || c.includes("medical")) return "ems";
  if (c.includes("search") || c.includes("rescue")) return "sr";
  if (c.includes("collision")) return "col";
  if (c.includes("fire")) return "ff";
  return "gen";
}

// ── Category palette ──────────────────────────────────────────────────────────
function makeRmCats(tPrimary = "var(--color-primary)") {
  return {
    ff:  { label: "Firefighter",     color: "#C2410C", bg: "rgba(194,65,12,.12)",  border: "rgba(194,65,12,.30)" },
    ems: { label: "EMS",             color: "var(--color-primary)", bg: "rgba(37,99,235,.12)",  border: "rgba(37,99,235,.28)" },
    hm:  { label: "Hazmat",          color: "#B5651D", bg: "rgba(181,101,29,.13)", border: "rgba(181,101,29,.30)" },
    fo:  { label: "Fire Officer",    color: tPrimary,  bg: alpha(tPrimary, 0.10),  border: alpha(tPrimary, 0.26) },
    sr:  { label: "Search & Rescue", color: "#15803D", bg: "rgba(21,128,61,.12)",  border: "rgba(21,128,61,.28)" },
    col: { label: "Collision",       color: "var(--color-accent-violet)", bg: "rgba(124,58,237,.10)", border: "rgba(124,58,237,.26)" },
    gen: { label: "General",         color: "var(--color-ink-2)", bg: "rgba(74,82,99,.10)",   border: "rgba(74,82,99,.22)" },
  };
}

// ── Room types ────────────────────────────────────────────────────────────────
const ROOM_TYPES = [
  { id: "classroom",  label: "Classroom" },
  { id: "lab",        label: "Lab" },
  { id: "sim",        label: "Simulation" },
  { id: "bay",        label: "Apparatus Bay" },
  { id: "conference", label: "Conference" },
  { id: "practical",  label: "Practical / Outdoor" },
];

const typeLabel = (id) => (ROOM_TYPES.find((t) => t.id === id) || {}).label || id;

// ── Schedule constants ────────────────────────────────────────────────────────
const DAY_START = 7 * 60;
const DAY_END   = 19 * 60;
const HOUR_W    = 150;

// ── Helpers ───────────────────────────────────────────────────────────────────
const toMin = (s) => { const [h, m] = s.split(":").map(Number); return h * 60 + m; };
const fmtTime = (min) => {
  const h = Math.floor(min / 60), m = min % 60, hr = h % 12 || 12;
  return `${hr}${m ? ":" + String(m).padStart(2, "0") : ""} ${h >= 12 ? "PM" : "AM"}`;
};
const fmtRange = (s, e) => `${fmtTime(toMin(s))} – ${fmtTime(toMin(e))}`;
const xForMin  = (min) => ((min - DAY_START) / 60) * HOUR_W;

// ── Collision logic ───────────────────────────────────────────────────────────
function computeRoomLayout(classes) {
  const sorted = [...classes].sort((a, b) => toMin(a.start) - toMin(b.start));
  const lanes = [], result = [];
  for (const c of sorted) {
    const s = toMin(c.start), e = toMin(c.end);
    let lane = lanes.findIndex((end) => s >= end);
    if (lane === -1) { lane = lanes.length; lanes.push(e); } else lanes[lane] = e;
    result.push({ ...c, _lane: lane });
  }
  const collide = new Set();
  for (let i = 0; i < result.length; i++)
    for (let j = i + 1; j < result.length; j++) {
      const a = result[i], b = result[j];
      if (toMin(a.start) < toMin(b.end) && toMin(b.start) < toMin(a.end)) { collide.add(a.id); collide.add(b.id); }
    }
  return { events: result, laneCount: Math.max(1, lanes.length), collide };
}

function findCollisions(rooms, classes) {
  const out = [];
  for (const room of rooms) {
    const rc = classes.filter((c) => c.roomId === room.id);
    for (let i = 0; i < rc.length; i++)
      for (let j = i + 1; j < rc.length; j++) {
        const a = rc[i], b = rc[j];
        if (a.sessionDate && b.sessionDate && a.sessionDate !== b.sessionDate) continue;
        if (toMin(a.start) < toMin(b.end) && toMin(b.start) < toMin(a.end)) out.push({ room, a, b });
      }
  }
  return out;
}

function roomConflictsFor(roomId, start, end, classes, excludeId) {
  const s = toMin(start), e = toMin(end);
  return classes.filter((c) => c.roomId === roomId && c.id !== excludeId && toMin(c.start) < e && s < toMin(c.end));
}

// ── Shared helpers ────────────────────────────────────────────────────────────
const inputStyle = {
  width: "100%", padding: "9px 12px", boxSizing: "border-box",
  border: "1.5px solid var(--color-hairline-cloud)", borderRadius: "9px",
  fontSize: "13.5px", color: "var(--color-ink)",
  background: "#fff", fontFamily: "inherit", outline: "none",
};

function SectionLabel({ children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: "10px" }}>
      <Typography sx={{ fontSize: "10.5px", fontWeight: 700, color: TC.ink4, textTransform: "uppercase", letterSpacing: ".1em", whiteSpace: "nowrap" }}>
        {children}
      </Typography>
      <Box sx={{ flex: 1, height: 1, background: TC.line2 }} />
    </Box>
  );
}

function getWeekDays(anchorDate) {
  const dow = anchorDate.getDay();
  const monday = new Date(anchorDate);
  monday.setDate(anchorDate.getDate() - ((dow + 6) % 7));
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function WeekStrip({ selectedDate, onSelect }) {
  const TC = makeTC(useTheme());
  const days = getWeekDays(selectedDate);
  const todayStr = new Date().toDateString();
  return (
    <Box sx={{ display: "flex", gap: "4px", mb: "10px" }}>
      {days.map((d, i) => {
        const isSelected = d.toDateString() === selectedDate.toDateString();
        const isToday    = d.toDateString() === todayStr;
        return (
          <Box
            key={i}
            component="button"
            onClick={() => onSelect(d)}
            sx={{
              flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
              gap: "2px", py: "7px", px: "4px", border: "1.5px solid",
              borderColor: isSelected ? TC.primary : TC.line,
              borderRadius: "10px", cursor: "pointer", fontFamily: "inherit",
              background: isSelected ? TC.primary10 : isToday ? TC.surface3 : "#fff",
              "&:hover:not(:disabled)": { background: isSelected ? TC.primary10 : TC.surface3 },
            }}
          >
            <Typography sx={{ fontSize: "10px", fontWeight: 700, color: isSelected ? TC.primary : TC.ink4, textTransform: "uppercase", letterSpacing: ".06em" }}>
              {DAY_LABELS[i]}
            </Typography>
            <Typography sx={{ fontSize: "15px", fontWeight: 800, color: isSelected ? TC.primary : isToday ? TC.ink : TC.ink2 }}>
              {d.getDate()}
            </Typography>
            {isToday && (
              <Box sx={{ width: 4, height: 4, borderRadius: "50%", background: isSelected ? TC.primary : TC.ink3 }} />
            )}
          </Box>
        );
      })}
    </Box>
  );
}

// ── SiteSwitcher ──────────────────────────────────────────────────────────────
function SiteSwitcher({ sites, rooms, current, setCurrent, onAddSite, onEditSite, onDeleteSite }) {
  const TC = makeTC(useTheme());
  const tabSx = (active) => ({
    display: "inline-flex", alignItems: "center", gap: "5px",
    px: "12px", py: "6px", border: "none", borderRadius: "20px",
    cursor: "pointer", fontFamily: "inherit", fontSize: "13px", fontWeight: 600,
    whiteSpace: "nowrap", transition: "background .12s",
    background: active ? TC.primary : "transparent",
    color: active ? "#fff" : TC.ink3,
    "&:hover": { background: active ? TC.primary : TC.surface3 },
  });
  const countSx = (active) => ({
    fontSize: "11px", fontWeight: 700, px: "5px", borderRadius: "10px",
    background: active ? "rgba(255,255,255,.22)" : TC.primary10,
    color: active ? "#fff" : TC.primary,
  });

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, overflowX: "auto", pb: 1, mb: 2, borderBottom: `1px solid ${TC.line}` }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "5px", mr: 1, flexShrink: 0 }}>
        <BusinessIcon sx={{ fontSize: 14, color: TC.ink4 }} />
        <Typography sx={{ fontSize: "11px", fontWeight: 700, color: TC.ink4, letterSpacing: ".08em", textTransform: "uppercase" }}>Site</Typography>
      </Box>
      <Box sx={{ display: "flex", gap: "4px" }}>
        <Box component="button" onClick={() => setCurrent("all")} sx={tabSx(current === "all")}>
          All Sites <Box component="span" sx={countSx(current === "all")}>{rooms.length}</Box>
        </Box>
        {sites.map((s) => {
          const active = String(current) === String(s.id);
          const n = rooms.filter((r) => r.siteId === s.id).length;
          return (
            <Box key={s.id} sx={{ display: "inline-flex", alignItems: "center", borderRadius: "20px", background: active ? TC.primary : "transparent", "&:hover": { background: active ? TC.primary : TC.surface3 } }}>
              <Box component="button" onClick={() => setCurrent(s.id)} sx={{ ...tabSx(active), background: "transparent", pr: "5px", "&:hover": { background: "transparent" } }}>
                <Box component="span" sx={{ fontSize: "10px", fontWeight: 800, px: "4px", borderRadius: "4px", background: active ? "rgba(255,255,255,.25)" : TC.primary10, color: active ? "#fff" : TC.primary, letterSpacing: ".05em" }}>
                  {s.code}
                </Box>
                {s.name}
                <Box component="span" sx={countSx(active)}>{n}</Box>
              </Box>
              <IconButton
                size="small"
                aria-label={`Edit ${s.name}`}
                onClick={() => onEditSite(s)}
                sx={{
                  width: 24, height: 24, color: active ? "rgba(255,255,255,.82)" : TC.ink4,
                  "&:hover": { background: active ? "rgba(255,255,255,.16)" : TC.primary10, color: active ? "#fff" : TC.primary },
                }}
              >
                <EditIcon sx={{ fontSize: 13 }} />
              </IconButton>
              <IconButton
                size="small"
                aria-label={`Delete ${s.name}`}
                onClick={() => onDeleteSite(s)}
                sx={{
                  width: 24, height: 24, mr: "3px", color: active ? "rgba(255,255,255,.82)" : TC.ink4,
                  "&:hover": { background: active ? "rgba(255,255,255,.16)" : "rgba(180,35,24,.10)", color: active ? "#fff" : "#B42318" },
                }}
              >
                <DeleteIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Box>
          );
        })}
      </Box>
      <Box sx={{ flex: 1 }} />
      <Button size="small" startIcon={<AddIcon />} onClick={onAddSite} sx={{ flexShrink: 0, color: TC.ink3, border: `1px solid ${TC.line}`, borderRadius: "8px", fontSize: "12px", fontWeight: 600, px: 1.5, py: "5px", "&:hover": { background: TC.surface3 } }}>
        Add Site
      </Button>
    </Box>
  );
}

// ── StatRow ───────────────────────────────────────────────────────────────────
function StatRow({ rooms, classes, collisions, nowMin }) {
  const TC = makeTC(useTheme());
  const inUse = rooms.filter((r) => classes.some((c) => c.roomId === r.id && toMin(c.start) <= nowMin && nowMin < toMin(c.end))).length;
  const stats = [
    { v: rooms.length,         label: "Rooms",          bg: TC.primary10,  color: TC.primary, Icon: MeetingRoomIcon },
    { v: inUse,                label: "In Use Now",      bg: TC.green10,    color: TC.green,   Icon: AccessTimeIcon },
    { v: rooms.length - inUse, label: "Available Now",   bg: TC.amber10,    color: TC.amber,   Icon: CheckCircleIcon },
    { v: collisions.length,    label: "Room Collisions", bg: TC.orange10,   color: TC.orange,  Icon: WarningAmberIcon, alert: collisions.length > 0 },
  ];
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 2, mb: 2 }}>
      {stats.map((s) => (
        <Box key={s.label} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: "14px 16px", background: "#fff", border: `1px solid ${s.alert ? "#C2410C" : TC.line}`, borderRadius: "12px", boxShadow: "0 1px 3px rgba(20,24,40,.06)" }}>
          <Box sx={{ width: 40, height: 40, borderRadius: "10px", display: "grid", placeItems: "center", background: s.bg, color: s.color, flexShrink: 0 }}>
            <s.Icon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "22px", fontWeight: 800, lineHeight: 1, color: s.alert ? "#C2410C" : TC.ink, fontFamily: "var(--font-display)" }}>{s.v}</Typography>
            <Typography sx={{ fontSize: "12px", color: TC.ink3, mt: "2px" }}>{s.label}</Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// ── CollisionBanner ───────────────────────────────────────────────────────────
function CollisionBanner({ collisions, onResolve }) {
  const TC = makeTC(useTheme());
  const RM_CATS = makeRmCats(TC.primary);
  if (collisions.length === 0) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: "12px 16px", mb: 2, borderRadius: "10px", background: TC.green10, border: `1px solid ${alpha(TC.green, 0.20)}` }}>
        <CheckCircleIcon sx={{ color: TC.green, fontSize: 20 }} />
        <Typography sx={{ fontSize: "13.5px", color: "#14532D" }}>
          <strong>No room collisions.</strong> Every class today has a clear room and time slot.
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ p: "14px 16px", mb: 2, borderRadius: "10px", background: "rgba(194,65,12,.05)", border: "1.5px solid rgba(194,65,12,.25)" }}>
      <Box sx={{ display: "flex", gap: 1.5, mb: 1.5 }}>
        <WarningAmberIcon sx={{ color: "#C2410C", fontSize: 20, flexShrink: 0, mt: "2px" }} />
        <Box>
          <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#9A2D08", fontFamily: "var(--font-display)" }}>
            {collisions.length} room collision{collisions.length > 1 ? "s" : ""} detected today
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "#C2410C", mt: "2px" }}>
            Two classes are booked into the same room at overlapping times. Reassign one to clear it.
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {collisions.map((col, i) => {
          const ca = RM_CATS[col.a.cat], cb = RM_CATS[col.b.cat];
          const ov = `${fmtTime(Math.max(toMin(col.a.start), toMin(col.b.start)))}–${fmtTime(Math.min(toMin(col.a.end), toMin(col.b.end)))}`;
          return (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: "10px 12px", borderRadius: "8px", background: "rgba(194,65,12,.07)", flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "5px", flexShrink: 0 }}>
                <MeetingRoomIcon sx={{ fontSize: 14, color: TC.ink3 }} />
                <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: TC.ink2 }}>{col.room.name}</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flex: 1, flexWrap: "wrap" }}>
                <Typography component="span" sx={{ fontSize: "12.5px", fontWeight: 600, color: ca.color }}>{col.a.title}</Typography>
                <Typography component="span" sx={{ fontSize: "11px", color: TC.ink4 }}>×</Typography>
                <Typography component="span" sx={{ fontSize: "12.5px", fontWeight: 600, color: cb.color }}>{col.b.title}</Typography>
                <Box sx={{ fontSize: "11px", fontWeight: 600, px: "6px", py: "1px", borderRadius: "6px", background: "rgba(194,65,12,.12)", color: "#9A2D08", whiteSpace: "nowrap" }}>
                  overlap {ov}
                </Box>
              </Box>
              <Button size="small" startIcon={<SwapHorizIcon />} onClick={() => onResolve(col)} sx={{ fontSize: "12px", fontWeight: 700, color: "#C2410C", border: "1px solid rgba(194,65,12,.35)", borderRadius: "7px", px: 1.5, py: "4px", flexShrink: 0, "&:hover": { background: "rgba(194,65,12,.10)" } }}>
                Resolve
              </Button>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ── Toolbar ───────────────────────────────────────────────────────────────────
function Toolbar({ view, setView, onAdd, date, onPrev, onNext, noSites }) {
  const TC = makeTC(useTheme());
  const weekDays = getWeekDays(date);
  const weekStart = weekDays[0];
  const weekEnd = weekDays[6];
  const isWeek = view === "week";
  const dayName = date.toLocaleDateString("en-US", { weekday: "long" });
  const dateStr = date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  const weekLabel = (() => {
    const sameMonth = weekStart.getMonth() === weekEnd.getMonth();
    const sameYear = weekStart.getFullYear() === weekEnd.getFullYear();
    const startPart = weekStart.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      ...(sameYear ? {} : { year: "numeric" }),
    });
    const endPart = weekEnd.toLocaleDateString("en-US", {
      month: sameMonth ? undefined : "short",
      day: "numeric",
      year: "numeric",
    });
    return `${startPart} – ${endPart}`;
  })();
  const isToday = date.toDateString() === new Date().toDateString();
  const todayInWeek = weekDays.some((d) => d.toDateString() === new Date().toDateString());
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2, py: "10px", borderTop: `1px solid ${TC.line}`, borderBottom: `1px solid ${TC.line}` }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <IconButton
          size="small"
          sx={{ color: TC.ink3 }}
          onClick={onPrev}
          aria-label={isWeek ? "Previous week" : "Previous day"}
        >
          <ChevronLeftIcon />
        </IconButton>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <CalendarTodayIcon sx={{ fontSize: 15, color: TC.ink3 }} />
          <Typography sx={{ fontSize: "14px", fontWeight: 600, color: TC.ink }}>
            {isWeek ? weekLabel : `${dayName}, ${dateStr}`}
          </Typography>
          {(isWeek ? todayInWeek : isToday) && (
            <Box sx={{ fontSize: "11px", fontWeight: 700, px: "7px", py: "2px", borderRadius: "6px", background: TC.primary10, color: TC.primary }}>
              Today
            </Box>
          )}
        </Box>
        <IconButton
          size="small"
          sx={{ color: TC.ink3 }}
          onClick={onNext}
          aria-label={isWeek ? "Next week" : "Next day"}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box sx={{ display: "flex", p: "3px", background: TC.surface3, border: `1.5px solid ${TC.line}`, borderRadius: "10px" }}>
          {[["schedule", "Schedule", CalendarTodayIcon], ["week", "Week", CalendarTodayIcon], ["rooms", "Rooms", GridViewIcon]].map(([v, lbl, Ic]) => (
            <Box key={v} component="button" onClick={() => setView(v)} sx={{ display: "flex", alignItems: "center", gap: "6px", px: "12px", py: "6px", border: "none", cursor: "pointer", borderRadius: "7px", fontFamily: "inherit", fontSize: "13px", fontWeight: 600, background: view === v ? "#fff" : "transparent", color: view === v ? TC.ink : TC.ink3, boxShadow: view === v ? "0 1px 3px rgba(20,24,40,.10)" : "none" }}>
              <Ic sx={{ fontSize: 14 }} />{lbl}
            </Box>
          ))}
        </Box>
        <Tooltip title={noSites ? "Create a site before adding rooms." : ""}>
          <span>
            <Button variant="contained" startIcon={<AddIcon />} onClick={onAdd} disabled={noSites} sx={{ background: TC.primary, borderRadius: "9px", fontSize: "13px", fontWeight: 700, px: 2, py: "7px", boxShadow: `0 2px 8px -2px ${alpha(TC.primary, 0.4)}`, "&:hover": { background: TC.primary700 } }}>
              Add Room
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}

// ── ScheduleView ──────────────────────────────────────────────────────────────
function ScheduleView({ rooms, classes, onClassClick, sitesById, showSite, nowMin }) {
  const TC = makeTC(useTheme());
  const RM_CATS = makeRmCats(TC.primary);
  const hours = [];
  for (let m = DAY_START; m < DAY_END; m += 60) hours.push(m);
  const trackW = ((DAY_END - DAY_START) / 60) * HOUR_W;
  const LANE_H = 56, LANE_GAP = 6, PAD_V = 9;

  return (
    <Box sx={{ overflowX: "auto", border: `1px solid ${TC.line}`, borderRadius: "12px", mb: 2 }}>
      <Box sx={{ minWidth: 220 + trackW }}>
        {/* Header */}
        <Box sx={{ display: "flex", borderBottom: `1px solid ${TC.line}`, background: TC.surface2 }}>
          <Box sx={{ width: 220, flexShrink: 0, px: 2, py: "10px", borderRight: `1px solid ${TC.line}`, fontSize: "11px", fontWeight: 700, color: TC.ink4, textTransform: "uppercase", letterSpacing: ".08em" }}>
            Room
          </Box>
          <Box sx={{ position: "relative", width: trackW, flexShrink: 0 }}>
            <Box sx={{ display: "flex" }}>
              {hours.map((m) => (
                <Box key={m} sx={{ width: HOUR_W, flexShrink: 0, px: "8px", py: "10px", fontSize: "11px", fontWeight: 600, color: TC.ink3, borderRight: `1px solid ${TC.line2}` }}>
                  {fmtTime(m)}
                </Box>
              ))}
            </Box>
            {nowMin >= DAY_START && nowMin < DAY_END && (
              <Box sx={{ position: "absolute", top: 0, left: xForMin(nowMin), fontSize: "10px", fontWeight: 700, color: "#fff", background: "#C2410C", px: "5px", py: "2px", borderRadius: "0 0 4px 4px", transform: "translateX(-50%)", zIndex: 10 }}>
                Now
              </Box>
            )}
          </Box>
        </Box>

        {/* Room rows */}
        {rooms.map((room) => {
          const rc = classes.filter((c) => c.roomId === room.id);
          const { events, laneCount, collide } = computeRoomLayout(rc);
          const rowH = laneCount * LANE_H + (laneCount - 1) * LANE_GAP + PAD_V * 2;
          return (
            <Box key={room.id} sx={{ display: "flex", height: rowH, borderBottom: `1px solid ${TC.line}`, "&:last-child": { borderBottom: "none" } }}>
              <Box sx={{ width: 220, flexShrink: 0, px: 2, py: "12px", borderRight: `1px solid ${TC.line}`, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <Typography sx={{ fontSize: "13px", fontWeight: 700, color: TC.ink }}>{room.name}</Typography>
                <Typography sx={{ fontSize: "11px", color: TC.ink3, mt: "2px" }}>#{room.number} · {typeLabel(room.type)} · {room.capacity} cap</Typography>
                {showSite && sitesById[room.siteId] && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: "4px", mt: "2px" }}>
                    <BusinessIcon sx={{ fontSize: 10, color: TC.ink4 }} />
                    <Typography sx={{ fontSize: "10.5px", color: TC.ink4 }}>{sitesById[room.siteId].name}</Typography>
                  </Box>
                )}
              </Box>
              <Box sx={{ position: "relative", flex: 1, background: "#fff" }}>
                {hours.map((m, i) => i > 0 && (
                  <Box key={m} sx={{ position: "absolute", top: 0, bottom: 0, left: i * HOUR_W, width: 1, background: TC.line2, pointerEvents: "none" }} />
                ))}
                {nowMin >= DAY_START && nowMin < DAY_END && (
                  <Box sx={{ position: "absolute", top: 0, bottom: 0, left: xForMin(nowMin), width: 1.5, background: "#C2410C", opacity: 0.7, pointerEvents: "none" }} />
                )}
                {events.map((ev) => {
                  const cat = RM_CATS[ev.cat];
                  const isC = collide.has(ev.id);
                  const left = xForMin(toMin(ev.start));
                  const width = Math.max(46, (toMin(ev.end) - toMin(ev.start)) / 60 * HOUR_W - 4);
                  const top = PAD_V + ev._lane * (LANE_H + LANE_GAP);
                  return (
                    <Box key={ev.id} component="button" onClick={() => onClassClick(ev)} sx={{ position: "absolute", left, top, width, height: LANE_H, display: "flex", alignItems: "stretch", border: `1.5px solid ${isC ? "#C2410C" : cat.border}`, borderRadius: "7px", background: isC ? "rgba(194,65,12,.07)" : cat.bg, cursor: "pointer", fontFamily: "inherit", overflow: "hidden", p: 0, "&:hover": { filter: "brightness(0.97)", zIndex: 5 } }}>
                      <Box sx={{ width: 3, background: isC ? "#C2410C" : cat.color, flexShrink: 0 }} />
                      <Box sx={{ flex: 1, px: "7px", py: "5px", display: "flex", flexDirection: "column", justifyContent: "center", overflow: "hidden" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          {isC && <WarningAmberIcon sx={{ fontSize: 11, color: "#C2410C", flexShrink: 0 }} />}
                          <Typography noWrap sx={{ fontSize: "12px", fontWeight: 700, color: isC ? "#9A2D08" : TC.ink }}>{ev.title}</Typography>
                        </Box>
                        <Typography noWrap sx={{ fontSize: "10.5px", color: TC.ink3, mt: "1px" }}>{fmtRange(ev.start, ev.end)} · {ev.instructor}</Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ── WeekScheduleView ───────────────────────────────────────────────────────────
function WeekScheduleView({ rooms, classes, weekDates, sitesById, showSite, onClassClick }) {
  const TC = makeTC(useTheme());
  const RM_CATS = makeRmCats(TC.primary);
  const byRoomDate = useMemo(() => {
    const map = new Map();
    for (const c of classes) {
      const key = `${c.roomId}|${c.sessionDate}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    }
    for (const events of map.values()) events.sort((a, b) => toMin(a.start) - toMin(b.start));
    return map;
  }, [classes]);

  return (
    <Box sx={{ overflowX: "auto", border: `1px solid ${TC.line}`, borderRadius: "12px", mb: 2 }}>
      <Box sx={{ minWidth: 220 + weekDates.length * 170 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: `220px repeat(${weekDates.length}, 170px)`, borderBottom: `1px solid ${TC.line}`, background: TC.surface2 }}>
          <Box sx={{ px: 2, py: "10px", borderRight: `1px solid ${TC.line}`, fontSize: "11px", fontWeight: 700, color: TC.ink4, textTransform: "uppercase", letterSpacing: ".08em" }}>Room</Box>
          {weekDates.map((d) => (
            <Box key={d.toISOString()} sx={{ px: 1.5, py: "10px", borderRight: `1px solid ${TC.line2}` }}>
              <Typography sx={{ fontSize: "11px", fontWeight: 800, color: TC.ink2 }}>{d.toLocaleDateString("en-US", { weekday: "short" })}</Typography>
              <Typography sx={{ fontSize: "11px", color: TC.ink3 }}>{d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}</Typography>
            </Box>
          ))}
        </Box>
        {rooms.map((room) => (
          <Box key={room.id} sx={{ display: "grid", gridTemplateColumns: `220px repeat(${weekDates.length}, 170px)`, minHeight: 118, borderBottom: `1px solid ${TC.line}`, "&:last-child": { borderBottom: "none" } }}>
            <Box sx={{ px: 2, py: "12px", borderRight: `1px solid ${TC.line}`, display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 700, color: TC.ink }}>{room.name}</Typography>
              <Typography sx={{ fontSize: "11px", color: TC.ink3, mt: "2px" }}>#{room.number} · {typeLabel(room.type)} · {room.capacity} cap</Typography>
              {showSite && sitesById[room.siteId] && <Typography sx={{ fontSize: "10.5px", color: TC.ink4, mt: "2px" }}>{sitesById[room.siteId].name}</Typography>}
            </Box>
            {weekDates.map((d) => {
              const dateKey = d.toISOString().split("T")[0];
              const events = byRoomDate.get(`${room.id}|${dateKey}`) || [];
              return (
                <Box key={`${room.id}-${dateKey}`} sx={{ p: "8px", borderRight: `1px solid ${TC.line2}`, background: "#fff", display: "flex", flexDirection: "column", gap: "6px" }}>
                  {events.map((ev) => {
                    const cat = RM_CATS[ev.cat] || RM_CATS.gen;
                    return (
                      <Box key={ev.id} component="button" onClick={() => onClassClick(ev)} sx={{ textAlign: "left", border: `1px solid ${cat.border}`, borderRadius: "7px", background: cat.bg, cursor: "pointer", p: "7px 8px", fontFamily: "inherit" }}>
                        <Typography noWrap sx={{ fontSize: "12px", fontWeight: 700, color: TC.ink }}>{ev.title}</Typography>
                        <Typography noWrap sx={{ fontSize: "10.5px", color: TC.ink3 }}>{fmtRange(ev.start, ev.end)}</Typography>
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ── RoomsGridView ─────────────────────────────────────────────────────────────
function RoomsGridView({ rooms, classes, onAdd, onClassClick, sitesById, showSite, nowMin, onEditRoom, onDeleteRoom }) {
  const TC = makeTC(useTheme());
  const RM_CATS = makeRmCats(TC.primary);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRoomId, setMenuRoomId] = useState(null);
  const menuRoom = rooms.find((r) => r.id === menuRoomId) || null;
  const closeMenu = () => { setMenuAnchorEl(null); setMenuRoomId(null); };

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 2, mb: 2 }}>
      {rooms.map((room) => {
        const rc = classes.filter((c) => c.roomId === room.id).sort((a, b) => toMin(a.start) - toMin(b.start));
        const { collide } = computeRoomLayout(rc);
        const hasC = rc.some((c) => collide.has(c.id));
        const busyNow = rc.some((c) => toMin(c.start) <= nowMin && nowMin < toMin(c.end));
        return (
          <Box key={room.id} sx={{ background: "#fff", border: `1.5px solid ${hasC ? "#C2410C" : TC.line}`, borderRadius: "12px", boxShadow: "0 1px 3px rgba(20,24,40,.06)", overflow: "hidden" }}>
            <Box sx={{ p: "14px 16px 10px", display: "flex", gap: 1.5 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: "9px", display: "grid", placeItems: "center", flexShrink: 0, background: TC.primary10, color: TC.primary }}>
                <MeetingRoomIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: "14px", fontWeight: 700, color: TC.ink }}>{room.name}</Typography>
                <Typography noWrap sx={{ fontSize: "11.5px", color: TC.ink3, mt: "1px" }}>
                  {showSite && sitesById[room.siteId] ? sitesById[room.siteId].name + " · " : ""}{room.building}, Floor {room.floor}
                </Typography>
              </Box>
              <IconButton
                size="small"
                sx={{ color: TC.ink4, alignSelf: "flex-start" }}
                onClick={(e) => { setMenuAnchorEl(e.currentTarget); setMenuRoomId(room.id); }}
              >
                <MoreHorizIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
            <Box sx={{ px: "16px", pb: "10px", display: "flex", flexWrap: "wrap", gap: "5px" }}>
              <Box sx={{ fontSize: "11px", fontWeight: 600, px: "7px", py: "3px", borderRadius: "6px", background: TC.primary10, color: TC.primary }}>{typeLabel(room.type)}</Box>
              <Box sx={{ fontSize: "11px", fontWeight: 600, px: "7px", py: "3px", borderRadius: "6px", background: TC.surface3, color: TC.ink3, display: "flex", alignItems: "center", gap: "3px" }}>
                <PeopleIcon sx={{ fontSize: 12 }} />{room.capacity} seats
              </Box>
              {busyNow ? (
                <Box sx={{ fontSize: "11px", fontWeight: 700, px: "7px", py: "3px", borderRadius: "6px", background: TC.green10, color: TC.green, display: "flex", alignItems: "center", gap: "4px" }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: TC.green }} />In use now
                </Box>
              ) : (
                <Box sx={{ fontSize: "11px", fontWeight: 600, px: "7px", py: "3px", borderRadius: "6px", background: TC.surface3, color: TC.ink3 }}>Available</Box>
              )}
            </Box>
            {room.equipment.length > 0 && (
              <Box sx={{ px: "16px", pb: "10px", display: "flex", flexWrap: "wrap", gap: "4px" }}>
                {room.equipment.map((e) => (
                  <Box key={e} sx={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "10.5px", color: TC.ink3, px: "6px", py: "2px", borderRadius: "5px", background: TC.surface3 }}>
                    <BuildIcon sx={{ fontSize: 10 }} />{e}
                  </Box>
                ))}
              </Box>
            )}
            <Box sx={{ mx: "16px", height: 1, background: TC.line2, mb: "10px" }} />
            <Box sx={{ px: "16px", pb: "6px", display: "flex", alignItems: "center", gap: "6px" }}>
              <Typography sx={{ fontSize: "11.5px", fontWeight: 600, color: TC.ink3 }}>{rc.length} class{rc.length !== 1 ? "es" : ""} today</Typography>
              {hasC && (
                <Box sx={{ display: "flex", alignItems: "center", gap: "3px", fontSize: "11px", color: "#C2410C" }}>
                  <WarningAmberIcon sx={{ fontSize: 12 }} />collision
                </Box>
              )}
            </Box>
            <Box sx={{ px: "16px", pb: "14px", display: "flex", flexDirection: "column", gap: "4px" }}>
              {rc.length === 0 ? (
                <Typography sx={{ fontSize: "12px", color: TC.ink4, py: "4px" }}>No classes assigned</Typography>
              ) : rc.map((c) => {
                const cat = RM_CATS[c.cat]; const isC = collide.has(c.id);
                return (
                  <Box key={c.id} component="button" onClick={() => onClassClick(c)} sx={{ display: "flex", alignItems: "center", border: `1px solid ${isC ? "rgba(194,65,12,.3)" : TC.line}`, borderRadius: "8px", background: isC ? "rgba(194,65,12,.05)" : TC.surface2, overflow: "hidden", cursor: "pointer", fontFamily: "inherit", p: 0, "&:hover": { background: isC ? "rgba(194,65,12,.08)" : TC.surface3 } }}>
                    <Box sx={{ width: 3, alignSelf: "stretch", background: isC ? "#C2410C" : cat.color, flexShrink: 0 }} />
                    <Box sx={{ flex: 1, px: "8px", py: "5px", textAlign: "left" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        {isC && <WarningAmberIcon sx={{ fontSize: 10, color: "#C2410C" }} />}
                        <Typography noWrap sx={{ fontSize: "12px", fontWeight: 600, color: TC.ink }}>{c.title}</Typography>
                      </Box>
                      <Typography sx={{ fontSize: "10.5px", color: TC.ink3 }}>{fmtRange(c.start, c.end)}</Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })}
      <Box component="button" onClick={onAdd} sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, p: 3, border: `2px dashed ${TC.line}`, borderRadius: "12px", background: "transparent", cursor: "pointer", fontFamily: "inherit", "&:hover": { background: TC.surface3, borderColor: TC.ink4 } }}>
        <Box sx={{ width: 40, height: 40, borderRadius: "50%", display: "grid", placeItems: "center", background: TC.primary10, color: TC.primary }}><AddIcon /></Box>
        <Typography sx={{ fontSize: "14px", fontWeight: 700, color: TC.ink, fontFamily: "var(--font-display)" }}>Add a room</Typography>
        <Typography sx={{ fontSize: "12px", color: TC.ink3 }}>Set up a new classroom, lab, or bay</Typography>
      </Box>
      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={closeMenu}>
        <MenuItem onClick={() => { onEditRoom(menuRoom); closeMenu(); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { onDeleteRoom(menuRoom); closeMenu(); }}>
          <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}

// ── RoomFormDialog (create + edit) ───────────────────────────────────────────
function RoomFormDialog({ mode = "create", initial, onClose, onSave, sites, defaultSite }) {
  const TC = makeTC(useTheme());
  const isEdit = mode === "edit";
  const [siteId, setSiteId] = useState(
    isEdit ? (initial?.siteId ?? "") : (defaultSite && defaultSite !== "all" ? defaultSite : (sites[0]?.id || ""))
  );
  const [name, setName] = useState(isEdit ? (initial?.name || "") : "");
  const [number, setNumber] = useState(isEdit ? (initial?.number || "") : "");
  const [building, setBuilding] = useState(isEdit ? (initial?.building || "") : "");
  const [floor, setFloor] = useState(isEdit ? (initial?.floor || "") : "");
  const [capacity, setCapacity] = useState(isEdit ? (initial?.capacity ?? 24) : 24);
  const [type, setType] = useState(isEdit ? (initial?.type || "classroom") : "classroom");
  const [equipment, setEquipment] = useState(isEdit ? (initial?.equipment || []) : []);
  const toggle = (e) => setEquipment((eq) => eq.includes(e) ? eq.filter((x) => x !== e) : [...eq, e]);
  // Tenant-configured feature list: {id, name}[]. Room tags no longer in the list stay selectable.
  const [features, setFeatures] = useState([]);
  const [managing, setManaging] = useState(false);
  const [newFeature, setNewFeature] = useState("");
  const [featureError, setFeatureError] = useState("");
  useEffect(() => {
    let live = true;
    Promise.resolve(fetchRoomFeatures()).then((l) => { if (live && Array.isArray(l)) setFeatures(l); }).catch(() => {});
    return () => { live = false; };
  }, []);
  const featureNames = [...features.map((f) => f.name), ...equipment.filter((e) => !features.some((f) => f.name === e))];
  const addFeature = async () => {
    const n = newFeature.trim();
    if (!n) return;
    try {
      const f = await addRoomFeature(n);
      setFeatures((l) => [...l, f].sort((a, b) => a.name.localeCompare(b.name)));
      setNewFeature(""); setFeatureError("");
    } catch (err) {
      setFeatureError(err?.response?.data?.error || "Could not add feature");
    }
  };
  const removeFeature = async (f) => {
    try {
      await deleteRoomFeature(f.id);
      setFeatures((l) => l.filter((x) => x.id !== f.id));
    } catch { setFeatureError("Could not remove feature"); }
  };
  const canSave = name.trim().length >= 2 && !!siteId;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, py: 2, borderBottom: `1px solid ${TC.line}` }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: "9px", display: "grid", placeItems: "center", background: TC.primary10, color: TC.primary }}><MeetingRoomIcon sx={{ fontSize: 18 }} /></Box>
          <Box>
            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: TC.ink4, textTransform: "uppercase", letterSpacing: ".08em" }}>Room Setup</Typography>
            <Typography sx={{ fontSize: "16px", fontWeight: 800, color: TC.ink, fontFamily: "var(--font-display)" }}>{isEdit ? "Edit Room" : "Add a Room"}</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 2, display: "flex", flexDirection: "column", gap: 2.5 }}>
        <Box>
          <SectionLabel>Site</SectionLabel>
          <Typography sx={{ fontSize: "12px", fontWeight: 600, color: TC.ink2, mb: "4px" }}>Training center site <span style={{ color: "#C2410C" }}>*</span></Typography>
          <select value={siteId} onChange={(e) => setSiteId(Number(e.target.value))} style={{ ...inputStyle }}>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.city}</option>)}
          </select>
        </Box>
        <Box>
          <SectionLabel>Identity</SectionLabel>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box>
              <Typography sx={{ fontSize: "12px", fontWeight: 600, color: TC.ink2, mb: "4px" }}>Room name <span style={{ color: "#C2410C" }}>*</span></Typography>
              <input style={inputStyle} placeholder="e.g. Classroom 103" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
              <Box>
                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: TC.ink2, mb: "4px" }}>Room number</Typography>
                <input style={{ ...inputStyle, fontFamily: "monospace" }} placeholder="e.g. 103" value={number} onChange={(e) => setNumber(e.target.value)} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: TC.ink2, mb: "4px" }}>Capacity</Typography>
                <Box sx={{ display: "flex", alignItems: "center", border: `1.5px solid ${TC.line}`, borderRadius: "9px", overflow: "hidden" }}>
                  <Box component="button" onClick={() => setCapacity((c) => Math.max(1, c - 1))} sx={{ width: 36, height: 38, border: "none", cursor: "pointer", background: TC.surface3, color: TC.ink2, fontSize: "16px", fontWeight: 700, fontFamily: "inherit", "&:hover": { background: TC.line } }}>−</Box>
                  <input type="number" value={capacity} onChange={(e) => { const n = parseInt(e.target.value); if (!isNaN(n)) setCapacity(n); }} style={{ flex: 1, border: "none", textAlign: "center", fontSize: "14px", fontWeight: 700, fontFamily: "inherit", background: "#fff", outline: "none", color: TC.ink, padding: "8px 4px" }} />
                  <Box component="button" onClick={() => setCapacity((c) => c + 1)} sx={{ width: 36, height: 38, border: "none", cursor: "pointer", background: TC.surface3, color: TC.ink2, fontSize: "16px", fontWeight: 700, fontFamily: "inherit", "&:hover": { background: TC.line } }}>+</Box>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
              <Box>
                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: TC.ink2, mb: "4px" }}>Building</Typography>
                <input style={inputStyle} placeholder="e.g. Main Building" value={building} onChange={(e) => setBuilding(e.target.value)} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: TC.ink2, mb: "4px" }}>Floor</Typography>
                <input style={inputStyle} placeholder="e.g. 1" value={floor} onChange={(e) => setFloor(e.target.value)} />
              </Box>
            </Box>
          </Box>
        </Box>
        <Box>
          <SectionLabel>Room type</SectionLabel>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "6px" }}>
            {ROOM_TYPES.map((t) => (
              <Box key={t.id} component="button" onClick={() => setType(t.id)} sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "5px", px: 1, py: "8px", border: `1.5px solid ${type === t.id ? TC.primary : TC.line}`, borderRadius: "9px", cursor: "pointer", fontFamily: "inherit", fontSize: "12px", fontWeight: 600, background: type === t.id ? TC.primary10 : "#fff", color: type === t.id ? TC.primary : TC.ink2 }}>
                {type === t.id && <CheckIcon sx={{ fontSize: 12 }} />}{t.label}
              </Box>
            ))}
          </Box>
        </Box>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <SectionLabel>Equipment</SectionLabel>
            <Button size="small" onClick={() => setManaging((m) => !m)} sx={{ fontSize: "11.5px", fontWeight: 700, textTransform: "none", color: TC.primary }}>
              {managing ? "Done" : "Manage features"}
            </Button>
          </Box>
          {managing && (
            <Box sx={{ mb: 1.5, p: 1.5, border: `1px dashed ${TC.line}`, borderRadius: "9px" }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mb: 1 }}>
                {features.map((f) => (
                  <Box key={f.id} sx={{ display: "flex", alignItems: "center", gap: "2px", pl: "9px", pr: "3px", py: "2px", border: `1px solid ${TC.line}`, borderRadius: "7px", fontSize: "12px", fontWeight: 600, color: TC.ink2 }}>
                    {f.name}
                    <IconButton size="small" aria-label={`Remove ${f.name}`} onClick={() => removeFeature(f)}><CloseIcon sx={{ fontSize: 12 }} /></IconButton>
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <input style={inputStyle} placeholder="New feature, e.g. Green Screen" value={newFeature} maxLength={100}
                  onChange={(e) => setNewFeature(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFeature(); } }} />
                <Button variant="outlined" size="small" onClick={addFeature} disabled={!newFeature.trim()}>Add feature</Button>
              </Box>
              {featureError && <Typography sx={{ fontSize: "12px", color: "#B42318", mt: 0.5 }}>{featureError}</Typography>}
            </Box>
          )}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
            {featureNames.map((e) => (
              <Box key={e} component="button" onClick={() => toggle(e)} sx={{ display: "flex", alignItems: "center", gap: "4px", px: "9px", py: "5px", border: `1.5px solid ${equipment.includes(e) ? TC.primary : TC.line}`, borderRadius: "7px", cursor: "pointer", fontFamily: "inherit", fontSize: "12px", fontWeight: 600, background: equipment.includes(e) ? TC.primary10 : "#fff", color: equipment.includes(e) ? TC.primary : TC.ink3 }}>
                {equipment.includes(e) ? <CheckIcon sx={{ fontSize: 11 }} /> : <AddIcon sx={{ fontSize: 11 }} />}{e}
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${TC.line}` }}>
        <Button onClick={onClose} sx={{ color: TC.ink3, fontWeight: 600 }}>Cancel</Button>
        <Button variant="contained" disabled={!canSave} onClick={() => onSave({ siteId, name: name.trim(), roomNumber: number, building, floor, capacity, roomType: type, equipment })} startIcon={<CheckIcon />} sx={{ background: TC.primary, borderRadius: "9px", fontWeight: 700, "&:hover": { background: TC.primary700 } }}>
          {isEdit ? "Save Changes" : "Create Room"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── DeleteRoomDialog ──────────────────────────────────────────────────────────
function DeleteRoomDialog({ room, affectedCount, onClose, onConfirm }) {
  const loadingCount = affectedCount === null;
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, py: 2, borderBottom: `1px solid ${TC.line}` }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: "9px", display: "grid", placeItems: "center", background: "rgba(180,35,24,.1)", color: "#B42318" }}><DeleteIcon sx={{ fontSize: 18 }} /></Box>
          <Box>
            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: TC.ink4, textTransform: "uppercase", letterSpacing: ".08em" }}>Delete Room</Typography>
            <Typography sx={{ fontSize: "16px", fontWeight: 800, color: TC.ink, fontFamily: "var(--font-display)" }}>{room.name}</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {loadingCount ? (
          <Typography sx={{ fontSize: "13px", color: TC.ink3 }}>Checking for assigned sessions…</Typography>
        ) : affectedCount > 0 ? (
          <Box sx={{ display: "flex", gap: 1, p: "10px 12px", borderRadius: "8px", background: "rgba(194,65,12,.07)", border: "1px solid rgba(194,65,12,.25)" }}>
            <WarningAmberIcon sx={{ fontSize: 16, color: "#C2410C", flexShrink: 0, mt: "1px" }} />
            <Typography sx={{ fontSize: "12.5px", color: "#9A2D08" }}>
              <strong>{affectedCount} session{affectedCount !== 1 ? "s" : ""}</strong> currently use this room and will lose their room assignment. This cannot be undone.
            </Typography>
          </Box>
        ) : (
          <Typography sx={{ fontSize: "13.5px", color: TC.ink2 }}>Delete this room? This cannot be undone.</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${TC.line}` }}>
        <Button onClick={onClose} sx={{ color: TC.ink3, fontWeight: 600 }}>Cancel</Button>
        <Button
          variant="contained"
          disabled={loadingCount}
          onClick={onConfirm}
          startIcon={<DeleteIcon />}
          sx={{ background: "#B42318", borderRadius: "9px", fontWeight: 700, "&:hover": { background: "#912016" } }}
        >
          Delete Room
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── DeleteSiteDialog ──────────────────────────────────────────────────────────
function DeleteSiteDialog({ site, impact, onClose, onConfirm }) {
  const loadingImpact = impact === null;
  const hasImpact = !loadingImpact && (impact.roomCount > 0 || impact.sessionCount > 0);
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, py: 2, borderBottom: `1px solid ${TC.line}` }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: "9px", display: "grid", placeItems: "center", background: "rgba(180,35,24,.1)", color: "#B42318" }}><DeleteIcon sx={{ fontSize: 18 }} /></Box>
          <Box>
            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: TC.ink4, textTransform: "uppercase", letterSpacing: ".08em" }}>Delete Site</Typography>
            <Typography sx={{ fontSize: "16px", fontWeight: 800, color: TC.ink, fontFamily: "var(--font-display)" }}>{site.name}</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {loadingImpact ? (
          <Typography sx={{ fontSize: "13px", color: TC.ink3 }}>Checking rooms and sessions at this site…</Typography>
        ) : hasImpact ? (
          <Box sx={{ display: "flex", gap: 1, p: "10px 12px", borderRadius: "8px", background: "rgba(194,65,12,.07)", border: "1px solid rgba(194,65,12,.25)" }}>
            <WarningAmberIcon sx={{ fontSize: 16, color: "#C2410C", flexShrink: 0, mt: "1px" }} />
            <Typography sx={{ fontSize: "12.5px", color: "#9A2D08" }}>
              This will also delete <strong>{impact.roomCount} room{impact.roomCount !== 1 ? "s" : ""}</strong>, and <strong>{impact.sessionCount} session{impact.sessionCount !== 1 ? "s" : ""}</strong> will lose their room assignment. This cannot be undone.
            </Typography>
          </Box>
        ) : (
          <Typography sx={{ fontSize: "13.5px", color: TC.ink2 }}>Delete this site? This cannot be undone.</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${TC.line}` }}>
        <Button onClick={onClose} sx={{ color: TC.ink3, fontWeight: 600 }}>Cancel</Button>
        <Button
          variant="contained"
          disabled={loadingImpact}
          onClick={onConfirm}
          startIcon={<DeleteIcon />}
          sx={{ background: "#B42318", borderRadius: "9px", fontWeight: 700, "&:hover": { background: "#912016" } }}
        >
          Delete Site
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── AddSiteDrawer ─────────────────────────────────────────────────────────────
function AddSiteDrawer({ mode = "create", initial, onClose, onSave }) {
  const TC = makeTC(useTheme());
  const isEdit = mode === "edit";
  const [name, setName] = useState(isEdit ? (initial?.name || "") : "");
  const [code, setCode] = useState(isEdit ? (initial?.code || "") : "");
  const [city, setCity] = useState(isEdit ? (initial?.city || "") : "");
  const [address, setAddress] = useState(isEdit ? (initial?.address || "") : "");
  const canSave = name.trim().length >= 2;
  const titleId = isEdit ? "edit-site-title" : "add-site-title";
  return (
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 2, borderBottom: `1px solid ${TC.line}` }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: "9px", display: "grid", placeItems: "center", background: TC.primary10, color: TC.primary }}><BusinessIcon sx={{ fontSize: 18 }} /></Box>
          <Box>
            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: TC.ink4, textTransform: "uppercase", letterSpacing: ".08em" }}>{isEdit ? "Site Details" : "New Location"}</Typography>
            <Typography id={titleId} sx={{ fontSize: "16px", fontWeight: 800, color: TC.ink, fontFamily: "var(--font-display)" }}>{isEdit ? "Edit Site" : "Add a Site"}</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </Box>
      <DialogContent sx={{ px: 2.5, py: 2, display: "flex", flexDirection: "column", gap: 2.5 }}>
        <Box sx={{ p: "11px 14px", borderRadius: "9px", background: TC.primary10, border: `1px solid ${alpha(TC.primary, 0.15)}` }}>
          <Typography sx={{ fontSize: "12.5px", color: TC.ink2, lineHeight: 1.5 }}>
            Sites are separate training-center locations under your tenant. Each site keeps its own classrooms, capacities, and schedule.
          </Typography>
        </Box>
        <Box>
          <SectionLabel>Site details</SectionLabel>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box>
              <Typography sx={{ fontSize: "12px", fontWeight: 600, color: TC.ink2, mb: "4px" }}>Site name <span style={{ color: "#C2410C" }}>*</span></Typography>
              <input aria-label="Site name" style={inputStyle} placeholder="e.g. West Training Annex" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
              <Box>
                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: TC.ink2, mb: "4px" }}>Site code</Typography>
                <input aria-label="Site code" style={{ ...inputStyle, fontFamily: "monospace" }} placeholder="e.g. WTA" value={code} onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 4))} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: TC.ink2, mb: "4px" }}>City</Typography>
                <input aria-label="City" style={inputStyle} placeholder="e.g. Westfield" value={city} onChange={(e) => setCity(e.target.value)} />
              </Box>
            </Box>
            <Box>
              <Typography sx={{ fontSize: "12px", fontWeight: 600, color: TC.ink2, mb: "4px" }}>Street address <Typography component="span" sx={{ fontSize: "11px", color: TC.ink4 }}>· optional</Typography></Typography>
              <input aria-label="Street address" style={inputStyle} placeholder="e.g. 88 Industrial Pkwy" value={address} onChange={(e) => setAddress(e.target.value)} />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 2, borderTop: `1px solid ${TC.line}`, gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "5px", flex: 1 }}>
          <BoltIcon sx={{ fontSize: 13, color: TC.ink4 }} />
          <Typography sx={{ fontSize: "11.5px", color: TC.ink3 }}>You can add rooms right after.</Typography>
        </Box>
        <Button onClick={onClose} sx={{ color: TC.ink3, fontWeight: 600 }}>Cancel</Button>
        <Button variant="contained" disabled={!canSave} onClick={() => onSave({ name: name.trim(), code: code || name.trim().slice(0, 3).toUpperCase(), city, address })} startIcon={<CheckIcon />} sx={{ background: TC.primary, borderRadius: "9px", fontWeight: 700, "&:hover": { background: TC.primary700 } }}>
          {isEdit ? "Save Changes" : "Create Site"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── ReassignPanel ─────────────────────────────────────────────────────────────
function ReassignPanel({ cls, rooms, classes, sites, onClose, onMove }) {
  const TC = makeTC(useTheme());
  const RM_CATS = makeRmCats(TC.primary);
  const cat = RM_CATS[cls.cat];
  const current = rooms.find((r) => r.id === cls.roomId);
  const conflictHere = roomConflictsFor(cls.roomId, cls.start, cls.end, classes, cls.id);
  const orderedSites = [...sites].sort((a) => (a.id === current?.siteId ? -1 : 1));

  return (
    <Drawer anchor="right" open onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 420 } } }}>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 2, borderBottom: `1px solid ${TC.line}` }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: "9px", display: "grid", placeItems: "center", background: cat.bg, color: cat.color }}><SwapHorizIcon sx={{ fontSize: 18 }} /></Box>
            <Box>
              <Typography sx={{ fontSize: "11px", fontWeight: 700, color: TC.ink4, textTransform: "uppercase", letterSpacing: ".08em" }}>Reassign Room</Typography>
              <Typography sx={{ fontSize: "16px", fontWeight: 800, color: TC.ink, fontFamily: "var(--font-display)" }}>Move this class</Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
        <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ display: "flex", border: `1.5px solid ${cat.border}`, borderRadius: "10px", overflow: "hidden" }}>
            <Box sx={{ width: 4, background: cat.color, flexShrink: 0 }} />
            <Box sx={{ flex: 1, px: 1.5, py: 1.5 }}>
              <Typography sx={{ fontSize: "14px", fontWeight: 700, color: TC.ink }}>{cls.title}</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: "5px", mt: "4px", flexWrap: "wrap" }}>
                <AccessTimeIcon sx={{ fontSize: 12, color: TC.ink3 }} />
                <Typography sx={{ fontSize: "12px", color: TC.ink3 }}>{fmtRange(cls.start, cls.end)}</Typography>
                <Typography sx={{ fontSize: "12px", color: TC.ink4 }}>·</Typography>
                <PeopleIcon sx={{ fontSize: 12, color: TC.ink3 }} />
                <Typography sx={{ fontSize: "12px", color: TC.ink3 }}>{cls.enrolled} enrolled</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: "5px", mt: "2px" }}>
                <LocationOnIcon sx={{ fontSize: 12, color: TC.ink3 }} />
                <Typography sx={{ fontSize: "12px", color: TC.ink3 }}>Currently in <strong>{current?.name}</strong></Typography>
              </Box>
            </Box>
          </Box>
          {conflictHere.length > 0 && (
            <Box sx={{ display: "flex", gap: 1, p: "10px 12px", borderRadius: "8px", background: "rgba(194,65,12,.07)", border: "1px solid rgba(194,65,12,.25)" }}>
              <WarningAmberIcon sx={{ fontSize: 16, color: "#C2410C", flexShrink: 0, mt: "1px" }} />
              <Typography sx={{ fontSize: "12.5px", color: "#9A2D08" }}>
                Collides with <strong>{conflictHere.map((c) => c.title).join(", ")}</strong> in {current?.name}. Pick a clear room below.
              </Typography>
            </Box>
          )}
          <SectionLabel>Move to</SectionLabel>
          {orderedSites.map((site) => {
            const siteRooms = rooms.filter((r) => r.siteId === site.id);
            if (siteRooms.length === 0) return null;
            const isCurSite = site.id === current?.siteId;
            return (
              <Box key={site.id} sx={{ mb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "6px" }}>
                  <BusinessIcon sx={{ fontSize: 13, color: TC.ink4 }} />
                  <Typography sx={{ fontSize: "12px", fontWeight: 700, color: TC.ink3 }}>{site.name}</Typography>
                  <Box sx={{ fontSize: "10.5px", fontWeight: 700, px: "6px", py: "2px", borderRadius: "5px", background: isCurSite ? TC.primary10 : TC.surface3, color: isCurSite ? TC.primary : TC.ink4 }}>
                    {isCurSite ? "Current site" : site.city}
                  </Box>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                  {siteRooms.map((room) => {
                    const conflicts = roomConflictsFor(room.id, cls.start, cls.end, classes, cls.id);
                    const free = conflicts.length === 0;
                    const isCurrent = room.id === cls.roomId;
                    const tooSmall = room.capacity < cls.enrolled;
                    const pillBg = isCurrent ? TC.primary10 : free ? TC.green10 : "rgba(194,65,12,.10)";
                    const pillColor = isCurrent ? TC.primary : free ? TC.green : "#C2410C";
                    return (
                      <Box key={room.id} component="button" disabled={isCurrent || !free} onClick={() => free && !isCurrent && onMove(room.id)} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: "10px 12px", border: `1.5px solid ${isCurrent ? TC.primary : free ? TC.line : "rgba(194,65,12,.20)"}`, borderRadius: "9px", cursor: isCurrent || !free ? "default" : "pointer", fontFamily: "inherit", background: isCurrent ? TC.primary10 : free ? "#fff" : "rgba(194,65,12,.04)", "&:hover:not(:disabled)": { background: free ? TC.surface3 : undefined } }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ width: 24, height: 24, borderRadius: "6px", display: "grid", placeItems: "center", background: pillBg, color: pillColor }}>
                            {isCurrent ? <LocationOnIcon sx={{ fontSize: 13 }} /> : free ? <CheckIcon sx={{ fontSize: 13 }} /> : <CloseIcon sx={{ fontSize: 13 }} />}
                          </Box>
                          <Box sx={{ textAlign: "left" }}>
                            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: TC.ink }}>{room.name}</Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                              <Typography sx={{ fontSize: "11px", color: TC.ink3 }}>{typeLabel(room.type)} · {room.capacity} cap</Typography>
                              {tooSmall && free && !isCurrent && <Typography sx={{ fontSize: "10.5px", color: TC.amber }}>· under capacity</Typography>}
                            </Box>
                          </Box>
                        </Box>
                        <Box sx={{ fontSize: "11px", fontWeight: 700, px: "8px", py: "3px", borderRadius: "6px", background: pillBg, color: pillColor }}>
                          {isCurrent ? "Current" : free ? "Available" : "Busy"}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2.5, py: 2, borderTop: `1px solid ${TC.line}` }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "5px", flex: 1 }}>
            <BoltIcon sx={{ fontSize: 13, color: TC.ink4 }} />
            <Typography sx={{ fontSize: "11.5px", color: TC.ink3 }}>Changes apply instantly to today's schedule.</Typography>
          </Box>
          <Button onClick={onClose} sx={{ color: TC.ink3, fontWeight: 600 }}>Close</Button>
        </Box>
      </Box>
    </Drawer>
  );
}

// ── BulkAssignBar ─────────────────────────────────────────────────────────────
function BulkAssignBar({ selectedCount, rooms, roomId, setRoomId, blockingSessions, checking, onAssign, assigning }) {
  const TC = makeTC(useTheme());
  const hasBlockers = blockingSessions.length > 0;
  const canAssign = !!roomId && !hasBlockers && !checking && !assigning;
  return (
    <Box sx={{ borderTop: `1px solid ${TC.line}`, px: 2.5, py: 2, display: "flex", flexDirection: "column", gap: "8px", background: TC.surface2 }}>
      <Typography sx={{ fontSize: "12px", fontWeight: 700, color: TC.ink2 }}>
        {selectedCount} session{selectedCount !== 1 ? "s" : ""} selected
      </Typography>
      <select
        value={roomId || ""}
        onChange={(e) => setRoomId(e.target.value ? Number(e.target.value) : null)}
        style={inputStyle}
        aria-label="Room to assign"
      >
        <option value="">Select a room…</option>
        {rooms.map((r) => (
          <option key={r.id} value={r.id}>{r.name} · {typeLabel(r.type)} · {r.capacity} cap</option>
        ))}
      </select>
      {checking && (
        <Typography sx={{ fontSize: "11.5px", color: TC.ink3 }}>Checking for conflicts…</Typography>
      )}
      {!checking && hasBlockers && (
        <Box sx={{ display: "flex", gap: 1, p: "8px 10px", borderRadius: "8px", background: "rgba(194,65,12,.07)", border: "1px solid rgba(194,65,12,.25)" }}>
          <WarningAmberIcon sx={{ fontSize: 14, color: "#C2410C", flexShrink: 0, mt: "1px" }} />
          <Typography sx={{ fontSize: "11.5px", color: "#9A2D08" }}>
            Conflicts with the target room: <strong>{blockingSessions.map((b) => `${b.classTitle || `Session ${b.id}`} (${b.sessionDate} ${(b.startTime || "").slice(0, 5)}–${(b.endTime || "").slice(0, 5)})`).join(", ")}</strong>. Deselect to continue.
          </Typography>
        </Box>
      )}
      <Button
        variant="contained"
        disabled={!canAssign}
        onClick={onAssign}
        startIcon={<CheckIcon />}
        sx={{ background: TC.primary, borderRadius: "9px", fontWeight: 700, "&:hover": { background: TC.primary700 } }}
      >
        {assigning ? "Assigning…" : "Assign to Room"}
      </Button>
    </Box>
  );
}

// ── UnassignedDrawer ──────────────────────────────────────────────────────────
function UnassignedDrawer({ open, onClose, sessions, rooms, onAssign, onBulkAssign }) {
  const TC = makeTC(useTheme());
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [roomId, setRoomId] = useState(null);
  const [blockingSessions, setBlockingSessions] = useState([]);
  const [checking, setChecking] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const sessionsById = useMemo(() => Object.fromEntries(sessions.map((s) => [s.id, s])), [sessions]);

  useEffect(() => {
    // Drop any selected ids that are no longer present (e.g. after a successful assign).
    setSelectedIds((ids) => {
      const next = new Set([...ids].filter((id) => sessionsById[id]));
      return next.size === ids.size ? ids : next;
    });
  }, [sessionsById]);

  useEffect(() => {
    if (!roomId || selectedIds.size === 0) {
      setBlockingSessions([]);
      return;
    }
    let cancelled = false;
    setChecking(true);
    const targets = [...selectedIds].map((id) => sessionsById[id]).filter(Boolean);
    Promise.all(
      targets.map((s) =>
        fetchRoomAssignmentCheck(roomId, {
          classId: s.classId,
          date: s.sessionDate,
          startTime: s.startTime,
          endTime: s.endTime,
          excludeSessionId: s.id,
        })
          .then((res) => (res.conflicts && res.conflicts.length > 0 ? s : null))
          .catch(() => null)
      )
    ).then((results) => {
      if (cancelled) return;
      setBlockingSessions(results.filter(Boolean));
    }).finally(() => {
      if (!cancelled) setChecking(false);
    });
    return () => { cancelled = true; };
  }, [roomId, selectedIds, sessionsById]);

  const toggleOne = (id) => {
    setSelectedIds((ids) => {
      const next = new Set(ids);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allSelected = sessions.length > 0 && selectedIds.size === sessions.length;
  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(sessions.map((s) => s.id)));
  };

  const handleClose = () => {
    setSelectedIds(new Set());
    setRoomId(null);
    setBlockingSessions([]);
    onClose();
  };

  const handleBulkAssign = async () => {
    setAssigning(true);
    try {
      const ok = await onBulkAssign(Array.from(selectedIds), roomId);
      if (ok) {
        setSelectedIds(new Set());
        setRoomId(null);
        setBlockingSessions([]);
      }
      // On failure (e.g. 409 race), selection and room choice are left intact.
    } finally {
      setAssigning(false);
    }
  };

  if (sessions.length === 0 && !open) return null;
  return (
    <Drawer anchor="right" open={open} onClose={handleClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 380 } } }}>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between",
          px: 2.5, py: 2, borderBottom: `1px solid ${TC.line}` }}>
          <Box>
            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: TC.ink4,
              textTransform: "uppercase", letterSpacing: ".08em" }}>Needs a Room</Typography>
            <Typography sx={{ fontSize: "16px", fontWeight: 800, color: TC.ink, fontFamily: "var(--font-display)" }}>
              Unassigned Sessions
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ fontSize: "12px", fontWeight: 700, px: "8px", py: "3px",
              borderRadius: "8px", background: sessions.length ? "rgba(180,35,24,.10)" : TC.surface3,
              color: sessions.length ? "#B42318" : TC.ink4 }}>
              {sessions.length} unassigned
            </Box>
            <IconButton size="small" onClick={handleClose}><CloseIcon /></IconButton>
          </Box>
        </Box>
        {sessions.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", px: 2.5, py: "10px", borderBottom: `1px solid ${TC.line}` }}>
            <Box component="label" sx={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
              <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Select all unassigned sessions" />
              <Typography sx={{ fontSize: "12px", fontWeight: 600, color: TC.ink2 }}>Select all</Typography>
            </Box>
          </Box>
        )}
        <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2,
          display: "flex", flexDirection: "column", gap: "8px" }}>
          {sessions.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography sx={{ fontSize: "14px", color: TC.ink3 }}>
                All sessions have rooms assigned.
              </Typography>
            </Box>
          ) : sessions.map((s) => (
            <Box key={s.id}
              sx={{ display: "flex", alignItems: "flex-start", gap: "10px", p: "12px 14px",
                border: `1.5px solid ${selectedIds.has(s.id) ? TC.primary : TC.line}`, borderRadius: "12px",
                background: selectedIds.has(s.id) ? TC.primary10 : "#fff", fontFamily: "inherit", textAlign: "left" }}>
              <Box sx={{ pt: "3px" }}>
                <input
                  type="checkbox"
                  checked={selectedIds.has(s.id)}
                  onChange={() => toggleOne(s.id)}
                  aria-label={`Select ${s.classTitle || `Session ${s.id}`}`}
                />
              </Box>
              <Box
                component="button"
                onClick={() => onAssign(s)}
                sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "flex-start", gap: "12px",
                  border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit",
                  textAlign: "left", p: 0 }}
              >
                <Box sx={{ width: 36, height: 36, borderRadius: "9px", background: TC.surface3,
                  display: "grid", placeItems: "center", flexShrink: 0, color: TC.ink4 }}>
                  <MeetingRoomIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: "13.5px", fontWeight: 700, color: TC.ink,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {s.classTitle || `Session ${s.id}`}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: TC.ink3, mt: "2px" }}>
                    {s.sessionDate} · {(s.startTime || "").slice(0,5)}&ndash;{(s.endTime || "").slice(0,5)}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mt: "4px" }}>
                    <Typography sx={{ fontSize: "11px", color: TC.ink4 }}>
                      {s.enrolled ?? 0} enrolled
                    </Typography>
                    {s.courseNumber && (
                      <Typography sx={{ fontSize: "11px", color: TC.ink4 }}>· {s.courseNumber}</Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ fontSize: "11px", fontWeight: 700, color: TC.primary, flexShrink: 0,
                  mt: "2px" }}>
                  Assign &rarr;
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
        {selectedIds.size > 0 && (
          <BulkAssignBar
            selectedCount={selectedIds.size}
            rooms={rooms}
            roomId={roomId}
            setRoomId={setRoomId}
            blockingSessions={blockingSessions}
            checking={checking}
            assigning={assigning}
            onAssign={handleBulkAssign}
          />
        )}
      </Box>
    </Drawer>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function RoomManagementPage() {
  const TC = makeTC(useTheme());
  const RM_CATS = makeRmCats(TC.primary);
  const { tenantId: activeTenantId } = useActiveTenant();
  const [sites, setSites]     = useState([]);
  const [rooms, setRooms]     = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView]     = useState("schedule");
  const [currentSite, setCurrentSite] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [addSiteOpen, setAddSiteOpen] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [editingRoom, setEditingRoom] = useState(null);
  const [deletingRoom, setDeletingRoom] = useState(null);
  const [deletingRoomAffectedCount, setDeletingRoomAffectedCount] = useState(null);
  const [deletingSite, setDeletingSite] = useState(null);
  const [deletingSiteImpact, setDeletingSiteImpact] = useState(null);
  const [reassign, setReassign] = useState(null);
  const [unassignedOpen, setUnassignedOpen] = useState(false);
  const [unassigned, setUnassigned]         = useState([]);
  const [toast, setToast]   = useState({ open: false, msg: "", severity: "success" });

  const today = useMemo(() => new Date(), []);
  const [selectedDate, setSelectedDate] = useState(today);
  const selectedDateStr = selectedDate.toISOString().split("T")[0];
  const nowMin = useMemo(() => {
    const sameDay = selectedDate.toDateString() === today.toDateString();
    return sameDay ? today.getHours() * 60 + today.getMinutes() : -1;
  }, [selectedDate, today]);

  const loadSchedule = useCallback((dateStr) => {
    setLoading(true);
    Promise.all([fetchSites(), fetchRoomsWithSchedule(dateStr)])
      .then(([sitesData, roomsData]) => {
        setSites(sitesData);
        setRooms(roomsData.map(({ sessions: _, ...r }) => r));
        const allSessions = roomsData.flatMap((room) =>
          (room.sessions || []).map((s) => ({
            id:           String(s.id),
            sessionId:    s.id,
            title:        s.classTitle || `Session ${s.id}`,
            cat:          getCatKey(s.category),
            roomId:       room.id,
            start:        (s.startTime || "08:00").slice(0, 5),
            end:          (s.endTime   || "09:00").slice(0, 5),
            instructor:   s.instructorName || "",
            enrolled:     s.enrolled || 0,
            capacity:     s.classCapacity || room.capacity || 30,
            classId:      s.classId,
            courseNumber: s.courseNumber || "",
            sessionDate:  s.sessionDate || "",
          }))
        );
        setClasses(allSessions);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const loadWeekSchedule = useCallback((date) => {
    setLoading(true);
    const weekDates = getWeekDays(date);
    const from = weekDates[0].toISOString().split("T")[0];
    const to = weekDates[6].toISOString().split("T")[0];
    Promise.all([fetchSites(), fetchRoomsWeekRange(from, to)])
      .then(([sitesData, roomsData]) => {
        setSites(sitesData);
        setRooms(roomsData.map(({ sessions: _, ...r }) => r));
        const allSessions = roomsData.flatMap((room) =>
          (room.sessions || []).map((s) => ({
            id:           String(s.id),
            sessionId:    s.id,
            title:        s.classTitle || `Session ${s.id}`,
            cat:          getCatKey(s.category),
            roomId:       room.id,
            start:        (s.startTime || "08:00").slice(0, 5),
            end:          (s.endTime   || "09:00").slice(0, 5),
            instructor:   s.instructorName || "",
            enrolled:     s.enrolled || 0,
            capacity:     s.classCapacity || room.capacity || 30,
            classId:      s.classId,
            courseNumber: s.courseNumber || "",
            sessionDate:  s.sessionDate || "",
          }))
        );
        setClasses(allSessions);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (view === "week") loadWeekSchedule(selectedDate);
    else loadSchedule(selectedDateStr);
  }, [selectedDate, selectedDateStr, view, loadSchedule, loadWeekSchedule]);

  const sitesById    = useMemo(() => Object.fromEntries(sites.map((s) => [s.id, s])), [sites]);
  const visibleRooms = useMemo(() => currentSite === "all" ? rooms : rooms.filter((r) => String(r.siteId) === String(currentSite)), [rooms, currentSite]);
  const visibleRoomIds = useMemo(() => new Set(visibleRooms.map((r) => r.id)), [visibleRooms]);
  const visibleClasses = useMemo(() => classes.filter((c) => visibleRoomIds.has(c.roomId)), [classes, visibleRoomIds]);
  const collisions   = useMemo(() => findCollisions(visibleRooms, classes), [visibleRooms, classes]);

  const flash = useCallback((msg, severity = "success") => setToast({ open: true, msg, severity }), []);

  const saveRoom = async (data) => {
    try {
      const created = await apiCreateRoom(data);
      setRooms((rs) => [...rs, created]);
      setAddOpen(false);
      flash(`${data.name} added to ${sitesById[data.siteId]?.name || "the site"}`);
    } catch {
      flash("Failed to save room", "error");
    }
  };

  const saveRoomEdit = async (roomId, data) => {
    try {
      const updated = await apiUpdateRoom(roomId, data);
      setRooms((rs) => rs.map((r) => (r.id === roomId ? updated : r)));
      setEditingRoom(null);
      flash(`${updated.name} updated`);
    } catch {
      flash("Failed to update room", "error");
    }
  };

  const startDeleteRoom = (room) => {
    setDeletingRoom(room);
    setDeletingRoomAffectedCount(null);
    fetchRoomSessionCount(room.id)
      .then((count) => setDeletingRoomAffectedCount(count))
      .catch(() => setDeletingRoomAffectedCount(0));
  };

  const confirmDeleteRoom = async () => {
    try {
      await apiDeleteRoom(deletingRoom.id);
      setRooms((rs) => rs.filter((r) => r.id !== deletingRoom.id));
      flash(`${deletingRoom.name} deleted`);
      setDeletingRoom(null);
    } catch {
      flash("Failed to delete room", "error");
    }
  };

  const saveSite = async (data) => {
    try {
      const created = await apiCreateSite(data);
      setSites((ss) => [...ss, created]);
      setAddSiteOpen(false);
      setCurrentSite(created.id);
      flash(`${created.name} added — add rooms to start scheduling`);
    } catch (err) {
      const msg = err?.response?.data?.error || "Failed to save site";
      flash(msg, "error");
    }
  };

  const saveSiteEdit = async (siteId, data) => {
    try {
      const updated = await apiUpdateSite(siteId, data);
      setSites((ss) => ss.map((s) => (s.id === siteId ? updated : s)));
      setEditingSite(null);
      flash(`${updated.name} updated`);
    } catch (err) {
      const msg = err?.response?.data?.error || "Failed to update site";
      flash(msg, "error");
    }
  };

  const startDeleteSite = (site) => {
    setDeletingSite(site);
    setDeletingSiteImpact(null);
    fetchSiteImpact(site.id)
      .then((impact) => setDeletingSiteImpact(impact))
      .catch(() => setDeletingSiteImpact({ roomCount: 0, sessionCount: 0 }));
  };

  const confirmDeleteSite = async () => {
    try {
      await apiDeleteSite(deletingSite.id);
      setSites((ss) => ss.filter((s) => s.id !== deletingSite.id));
      setRooms((rs) => rs.filter((r) => r.siteId !== deletingSite.id));
      if (String(currentSite) === String(deletingSite.id)) setCurrentSite("all");
      flash(`${deletingSite.name} deleted`);
      setDeletingSite(null);
    } catch {
      flash("Failed to delete site", "error");
    }
  };

  const moveClass = async (newRoomId) => {
    const target = rooms.find((r) => String(r.id) === String(newRoomId));
    try {
      await patchSessionRoom(reassign.sessionId, newRoomId);
      setClasses((cs) =>
        cs.map((c) => (c.id === reassign.id ? { ...c, roomId: newRoomId } : c))
      );
      setReassign(null);
      flash(`Moved "${reassign.title}" to ${target?.name ?? "new room"}`);
    } catch {
      flash("Failed to save room change. Please try again.", "error");
    }
  };

  const loadUnassigned = useCallback(() => {
    const from = selectedDateStr;
    const to   = new Date(new Date(selectedDate).setDate(selectedDate.getDate() + 30))
                   .toISOString().split("T")[0];
    fetchUnassignedSessions(from, to)
      .then(setUnassigned)
      .catch(() => {});
  }, [selectedDateStr, selectedDate]);

  useEffect(() => {
    if (unassignedOpen) loadUnassigned();
  }, [unassignedOpen, loadUnassigned]);

  const bulkAssign = async (sessionIds, roomId) => {
    try {
      await apiBulkAssignRoom(sessionIds, roomId);
      const idSet = new Set(sessionIds.map(String));
      setUnassigned((us) => us.filter((s) => !idSet.has(String(s.id))));
      // Refresh the schedule for the currently selected date so the assigned
      // sessions show up under their new room without a full page reload.
      loadSchedule(selectedDateStr);
      const target = rooms.find((r) => String(r.id) === String(roomId));
      flash(`${sessionIds.length} session${sessionIds.length !== 1 ? "s" : ""} assigned to ${target?.name ?? "room"}`);
      return true;
    } catch (err) {
      if (err?.response?.status === 409) {
        const conflictingIds = new Set((err.response.data?.conflictingSessionIds || []).map(String));
        const blocked = unassigned.filter((s) => conflictingIds.has(String(s.id)));
        const names = blocked.map((s) => s.classTitle || `Session ${s.id}`).join(", ");
        flash(
          names
            ? `Room conflict — could not assign: ${names}. Deselect and retry.`
            : "Room conflict — none of the selected sessions were assigned.",
          "error"
        );
      } else {
        flash("Failed to bulk-assign room. Please try again.", "error");
      }
      // Fail-closed on the backend means nothing was applied; keep selection intact.
      return false;
    }
  };

  const shiftDate = useCallback((days) => {
    setSelectedDate((d) => {
      const next = new Date(d);
      next.setDate(next.getDate() + days);
      return next;
    });
  }, []);

  if (!activeTenantId) {
    return (
      <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 2, sm: 4 }, py: { xs: 2, sm: 3.5 } }}>
        <Box sx={{ mb: 2.75 }}>
          <Typography sx={{ fontSize: { xs: 24, sm: 30 }, fontWeight: 600, letterSpacing: "-.01em", color: "var(--color-ink)", lineHeight: 1.15, fontFamily: "var(--font-display)" }}>
            Room Management
          </Typography>
        </Box>
        <NoTenantSelected
          icon={MeetingRoomIcon}
          message="Select a training center from the top bar to manage its rooms and sites."
        />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
        <Typography sx={{ color: TC.ink3 }}>Loading rooms…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 2, sm: 4 }, py: { xs: 2, sm: 3.5 }, pb: 12 }}>
      <Box sx={{ mb: 2.75 }}>
        <Typography sx={{ fontSize: { xs: 24, sm: 30 }, fontWeight: 600, letterSpacing: "-.01em", color: "var(--color-ink)", lineHeight: 1.15, fontFamily: "var(--font-display)" }}>
          Room Management
        </Typography>
        <Typography sx={{ mt: "7px", fontSize: 14, color: "var(--color-ink)", opacity: 0.6, maxWidth: "56ch" }}>
          Build out your training-center sites, assign classes to rooms, and catch booking collisions before they happen.
        </Typography>
      </Box>

      <SiteSwitcher sites={sites} rooms={rooms} current={currentSite} setCurrent={setCurrentSite} onAddSite={() => setAddSiteOpen(true)} onEditSite={setEditingSite} onDeleteSite={startDeleteSite} />
      <StatRow rooms={visibleRooms} classes={classes} collisions={collisions} nowMin={nowMin} />
      <CollisionBanner collisions={collisions} onResolve={(col) => setReassign(col.b)} />
      <WeekStrip selectedDate={selectedDate} onSelect={(d) => setSelectedDate(d)} />
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: "6px" }}>
        <Button
          variant="outlined"
          startIcon={<WarningAmberIcon sx={{ fontSize: 15 }} />}
          onClick={() => setUnassignedOpen(true)}
          sx={{
            height: 34, fontSize: "12.5px", fontWeight: 700, borderRadius: "9px",
            borderColor: unassigned.length ? "rgba(180,35,24,.4)" : TC.line,
            color: unassigned.length ? "#B42318" : TC.ink3,
            "&:hover": { background: "rgba(180,35,24,.06)", borderColor: "rgba(180,35,24,.4)" },
          }}
        >
          {unassigned.length ? `${unassigned.length} Unassigned` : "Unassigned"}
        </Button>
      </Box>
      <Toolbar
        view={view}
        setView={setView}
        onAdd={() => setAddOpen(true)}
        date={selectedDate}
        onPrev={() => shiftDate(view === "week" ? -7 : -1)}
        onNext={() => shiftDate(view === "week" ? 7 : 1)}
        noSites={sites.length === 0}
      />

      {sites.length === 0 && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: "10px" }}>No sites available. Create a site before adding rooms.</Alert>
      )}

      {visibleRooms.length === 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5, py: 8, border: `2px dashed ${TC.line}`, borderRadius: "14px", background: TC.surface2 }}>
          <Box sx={{ width: 52, height: 52, borderRadius: "14px", display: "grid", placeItems: "center", background: TC.primary10, color: TC.primary }}><MeetingRoomIcon sx={{ fontSize: 26 }} /></Box>
          <Typography sx={{ fontSize: "16px", fontWeight: 700, color: TC.ink, fontFamily: "var(--font-display)" }}>No rooms at this site yet</Typography>
          <Typography sx={{ fontSize: "13px", color: TC.ink3 }}>Add classrooms, labs, or bays to start scheduling here.</Typography>
          <Tooltip title={sites.length === 0 ? "Create a site before adding rooms." : ""}>
            <span>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)} disabled={sites.length === 0} sx={{ background: TC.primary, borderRadius: "9px", mt: 1, fontWeight: 700, "&:hover": { background: TC.primary700 } }}>
                Add Room
              </Button>
            </span>
          </Tooltip>
        </Box>
      ) : view === "schedule" ? (
        <ScheduleView rooms={visibleRooms} classes={visibleClasses} onClassClick={setReassign} sitesById={sitesById} showSite={currentSite === "all"} nowMin={nowMin} />
      ) : view === "week" ? (
        <WeekScheduleView rooms={visibleRooms} classes={visibleClasses} weekDates={getWeekDays(selectedDate)} onClassClick={setReassign} sitesById={sitesById} showSite={currentSite === "all"} />
      ) : (
        <RoomsGridView rooms={visibleRooms} classes={visibleClasses} onAdd={() => setAddOpen(true)} onClassClick={setReassign} sitesById={sitesById} showSite={currentSite === "all"} nowMin={nowMin} onEditRoom={setEditingRoom} onDeleteRoom={startDeleteRoom} />
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2, mb: 1, flexWrap: "wrap" }}>
        <Typography sx={{ fontSize: "11px", fontWeight: 700, color: TC.ink4, textTransform: "uppercase", letterSpacing: ".08em" }}>Categories</Typography>
        {Object.values(RM_CATS).map((c) => (
          <Box key={c.label} sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "2px", background: c.color }} />
            <Typography sx={{ fontSize: "12px", color: TC.ink3 }}>{c.label}</Typography>
          </Box>
        ))}
        <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "2px", background: "#C2410C" }} />
          <WarningAmberIcon sx={{ fontSize: 11, color: "#C2410C" }} />
          <Typography sx={{ fontSize: "12px", color: TC.ink3 }}>Collision</Typography>
        </Box>
      </Box>

      {addOpen && <RoomFormDialog mode="create" onClose={() => setAddOpen(false)} onSave={saveRoom} sites={sites} defaultSite={currentSite} />}
      {editingRoom && (
        <RoomFormDialog
          mode="edit"
          initial={editingRoom}
          onClose={() => setEditingRoom(null)}
          onSave={(data) => saveRoomEdit(editingRoom.id, data)}
          sites={sites}
          defaultSite={currentSite}
        />
      )}
      {deletingRoom && (
        <DeleteRoomDialog
          room={deletingRoom}
          affectedCount={deletingRoomAffectedCount}
          onClose={() => setDeletingRoom(null)}
          onConfirm={confirmDeleteRoom}
        />
      )}
      {deletingSite && (
        <DeleteSiteDialog
          site={deletingSite}
          impact={deletingSiteImpact}
          onClose={() => setDeletingSite(null)}
          onConfirm={confirmDeleteSite}
        />
      )}
      {addSiteOpen && <AddSiteDrawer onClose={() => setAddSiteOpen(false)} onSave={saveSite} />}
      {editingSite && (
        <AddSiteDrawer
          mode="edit"
          initial={editingSite}
          onClose={() => setEditingSite(null)}
          onSave={(data) => saveSiteEdit(editingSite.id, data)}
        />
      )}
      {reassign && <ReassignPanel cls={reassign} rooms={rooms} classes={classes} sites={sites} onClose={() => setReassign(null)} onMove={moveClass} />}
      <UnassignedDrawer
        open={unassignedOpen}
        onClose={() => setUnassignedOpen(false)}
        sessions={unassigned}
        rooms={rooms}
        onBulkAssign={bulkAssign}
        onAssign={(s) => {
          setUnassignedOpen(false);
          setReassign({
            id:         String(s.id),
            sessionId:  s.id,
            title:      s.classTitle || `Session ${s.id}`,
            cat:        getCatKey(s.category),
            roomId:     null,
            start:      (s.startTime || "08:00").slice(0, 5),
            end:        (s.endTime   || "09:00").slice(0, 5),
            enrolled:   s.enrolled || 0,
            capacity:   s.classCapacity || 30,
          });
        }}
      />

      <Snackbar open={toast.open} autoHideDuration={3200} onClose={() => setToast((t) => ({ ...t, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={() => setToast((t) => ({ ...t, open: false }))} severity={toast.severity} sx={{ borderRadius: "10px", fontWeight: 600 }}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
